<template>
    <div class="[ bg-gray-pale ]">
        <Teleport to="[data-header-action]" v-if="isMounted">
            <div class="[ flex gap-2 ]">
                <ButtonComponent outlined label="Export Template" @click="exportTemplate">
                    <template #icon>
                        <JBIcon name="file" />
                    </template>
                </ButtonComponent>
                <ButtonComponent outlined label="New Rule" @click="createNewRule(null)">
                    <template #icon>
                        <JBIcon name="add" />
                    </template>
                </ButtonComponent>
            </div>
        </Teleport>
        <div class="[ flex flex-col gap-2 pt-5 ]">
            <DefineRuleTemplate v-slot="{ rule }">
                <RuleReorderable :item="rule"
                    v-slot="{ isDraggedAbove, isDraggedUnder, events, bindings, isBeingDragged }">
                    <tr v-on="events" v-bind="bindings" class="[ bg-white border-t ]" :class="{
                        'border-t-8': isDraggedAbove,
                        'border-b-8': isDraggedUnder,
                        'opacity-40': isBeingDragged,
                    }" @click="openRule(rule)">
                        <td class="[ p-2 cursor-move ]">
                            <JBIcon name="menu" />
                        </td>
                        <td class="[ p-2 border-l ]">{{ rule.name }}</td>
                        <td class="[ pr-2 text-end border-l ]">{{ rule.maxScore }}</td>
                    </tr>
                </RuleReorderable>
            </DefineRuleTemplate>
            <table class="[ border ]">
                <thead>
                    <tr class="[ bg-white ]">
                        <th></th>
                        <th>Name</th>
                        <th class="[ min-w-32 border-l ]">Max Score</th>
                    </tr>
                </thead>
                <tbody>
                    <ReuseRuleTemplate v-for="rule in scorecardTemplate?.rules?.filter(x => !x.sectionId)"
                        :rule="rule" />
                </tbody>
                <SectionReorderable
                    v-slot="{ isDraggedAbove: isSectionDraggedAbove, isDraggedUnder: isSectionDraggedUnder, isBeingDragged: isSectionBeingDragged, events: sectionEvents, bindings: sectionBindings }"
                    v-for="section in scorecardTemplate?.sections" :item="section">
                    <tbody @dragover="sectionEvents.dragover" :class="{
                        'border-t-8': isSectionDraggedAbove,
                        'border-b-8': isSectionDraggedUnder,
                        'opacity-40': isSectionBeingDragged,
                    }">
                        <tr class="[ bg-gray-light border-t ]" @dragstart="sectionEvents.dragstart" @dragend="sectionEvents.dragend"
                            v-bind="sectionBindings">
                            <td class="[ p-2 cursor-move ]">
                                <JBIcon name="menu" />
                            </td>
                            <td class="[ p-2 ]">
                                <Inplace>
                                    <template #display>
                                        {{ section.name }}
                                    </template>
                                    <template #content="{ closeCallback }">
                                        <InputText v-model="section.name" @blur="closeCallback"
                                            @keydown="e => onSectionEditKeyDown(e, section, closeCallback)" autofocus />
                                    </template>
                                </Inplace>
                            </td>
                            <td class="[ pr-2 ]">
                                <div @click="createNewRule(section.id!)" class="[ flex flex-row items-center justify-end ]">
                                    <JBIcon name="add" />
                                </div>
                            </td>
                        </tr>
                        <RuleReorderable
                            v-if="!scorecardTemplate?.rules?.filter(x => x.sectionId === section.id!).length"
                            v-slot="{ events, isDraggedAbove, isDraggedUnder }"
                            :item="{ id: -1, sectionId: section.id }">
                            <tr @dragover="events.dragover">
                                <td colspan="3">
                                    <Empty icon="unavailable" title="Empty" message="Drop rules here" class="[ p-4 ]"
                                        :class="{
                                            'border': isDraggedAbove || isDraggedUnder
                                        }" />
                                </td>
                            </tr>
                        </RuleReorderable>
                        <ReuseRuleTemplate
                            v-for="rule in scorecardTemplate?.rules?.filter(x => x.sectionId === section.id)"
                            :rule="rule" />

                    </tbody>
                </SectionReorderable>
                <tbody>
                    <tr>
                        <td colspan="3">
                            <div class="[ flex items-center justify-center p-4 ]">
                                <ButtonComponent label="Create section" outlined @click="openNewSectionDialog">
                                    <template #icon>
                                        <JBIcon name="add" />
                                    </template>
                                </ButtonComponent>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <JBDialog v-model:visible="isNewSectionDialogOpen">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="newSectionName" />
                    </template>
                </GridFormField>
            </GridForm>
            <template #footer>
                <div class="[ flex justify-end ]">
                    <ButtonComponent label="Create" @click="createSection" :disabled="newSectionName.length < 4"
                        :loading="isCreatingSection" />
                </div>
            </template>
        </JBDialog>
        <JBDrawer v-model="isDrawerOpen" class="[ !w-[70vw] ]">
            <template #header>
                <div>{{ selectedRule?.name || 'New Rule' }}</div>
            </template>
            <div class="[ flex flex-col gap-2 ]">
                <GridForm size="full">
                    <GridFormField id="name" label="Name">
                        <template #input="{ id }">
                            <InputText :id="id" v-model="newRule.name" />
                        </template>
                    </GridFormField>
                    <GridFormField id="maxScore" label="Max Score">
                        <template #input="{ id }">
                            <InputNumber :id="id" v-model="newRule.maxScore" />
                        </template>
                    </GridFormField>
                    <GridFormField id="isManual" label="Is Manual">
                        <template #input="{ id }">
                            <ToggleSwitch :id="id" v-model="newRule.isManual" />
                        </template>
                    </GridFormField>
                    <GridFormField id="isScriptAdherence" label="Is Script Adherence">
                        <template #input="{ id }">
                            <ToggleSwitch :id="id" v-model="newRule.isScriptAdherence" />
                        </template>
                    </GridFormField>
                    <GridFormField id="type" label="Type">
                        <template #input="{ id }">
                            <Select :id="id" v-model="newRule.type"
                                :options="[{ label: 'Default', value: RuleType.Default }, { label: 'Penalty', value: RuleType.Penalty }, { label: 'Bonus', value: RuleType.Bonus }]"
                                option-label="label" option-value="value" />
                        </template>
                    </GridFormField>
                </GridForm>
                <div class="[ flex flex-col gap-2 ]">
                    <div>Script</div>
                    <MonacoEditor :model-value="newRule.scriptSource"
                        @update:model-value="newRule.scriptSource = $event" :libs="libs" class="[ h-96 ]"
                        language="javascript" />
                </div>
                <div class="[ flex-1 ]"></div>
                <ButtonComponent :label="selectedRule ? 'Update' : 'Create'" @click="saveNewRule" :loading="isSaving" />
            </div>
        </JBDrawer>
    </div>
</template>
<script setup lang="ts">
import { definePage } from 'vue-router/auto'
import { Button as ButtonComponent, InputNumber, InputText, ToggleSwitch, Inplace, Select } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { Rule, RuleType, Section } from '@/services/api/sdk';
import { defineComponent, onMounted, PropType, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { createReusableTemplate, useMounted } from '@vueuse/core';
import JBDrawer from '@/components/ui/modals/JBDrawer.vue';
import MonacoEditor from '@/components/ui/MonacoEditor.vue';
import GridForm from '@/components/ui/GridForm.vue';
import GridFormField from '@/components/ui/GridFormField.vue';
import { setBreadcrumbs } from '@/utils/breadcrumb';
import JBDialog from '@/components/ui/modals/JBDialog.vue';
import Empty from '@/components/ui/Empty.vue';

let _globalReOrderId = 0;

type ReOrderHandlerFn<T> = (src: T, dst: T, isUnder: boolean) => void;

function useReorder<T>(handler: ReOrderHandlerFn<T>) {
    const selected = ref<T | null>(null);
    const draggedOver = ref<T | null>(null);
    const isUnder = ref(false);
    const id = `dnd/${_globalReOrderId++}`;
    let escapedPressed = false;

    function trackEscapePress(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            escapedPressed = true;
        }
    }

    const Reorderable = defineComponent<{ item: T }>({
        props: {
            item: Object as PropType<T>
        },
        setup(props, { slots }) {
            function dragstart(e: DragEvent) {
                if (!e.dataTransfer) {
                    return;
                }
                e.dataTransfer.items.add(id, id);
                selected.value = props.item;
                document.addEventListener('keydown', trackEscapePress);
            }

            function dragover(e: DragEvent) {
                if (!e.dataTransfer || !e.dataTransfer.types.includes(id)) {
                    return;
                }
                draggedOver.value = props.item;
                if (e.target instanceof Element) {
                    const rect = e.target.getBoundingClientRect();
                    const center = rect.top + rect.height / 2;
                    isUnder.value = e.y > center;
                }
            }

            function dragend(e: DragEvent) {
                if (!e.dataTransfer || !e.dataTransfer.types.includes(id) || escapedPressed) {
                    return;
                }
                if (draggedOver.value !== selected.value) {
                    handler(selected.value, draggedOver.value, isUnder.value);
                }
                selected.value = null;
                draggedOver.value = null;
                isUnder.value = false;
                document.removeEventListener('keydown', trackEscapePress);
            }

            const events = {
                dragstart,
                dragover,
                dragend,
            };

            return () => {
                const isBeingDragged = props.item === selected.value;
                const isDraggedAbove = !isBeingDragged && props.item === draggedOver.value && !isUnder.value;
                const isDraggedUnder = !isBeingDragged && props.item === draggedOver.value && isUnder.value;

                return slots.default?.({
                    isDraggedAbove,
                    isDraggedUnder,
                    isBeingDragged,
                    events,
                    bindings: {
                        draggable: true
                    }
                });
            }
        }
    });

    return {
        Reorderable,
    };
}

const [DefineRuleTemplate, ReuseRuleTemplate] = createReusableTemplate<{ rule: Rule }>();

const {
    Reorderable: RuleReorderable,
} = useReorder<Rule>((src, dst, isUnder) => {
    if (dst.id === -1) {
        src.sectionId = dst.sectionId;
        src.order = 0;
        api.updateRule({
            ruleId: src.id!,
            updateRuleRequest: {
                sectionId: dst.sectionId!
            },
        });
    } else {
        const rules = [...scorecardTemplate.value?.rules!];
        const srcIndex = rules.indexOf(src);
        const dstIndex = rules.indexOf(dst);
        const removed = rules.splice(srcIndex, 1);
        rules.splice(dstIndex + (isUnder ? 1 : 0), 0, ...removed);
        scorecardTemplate.value!.rules = rules;
        if (src.sectionId !== dst.sectionId) {
            src.sectionId = dst.sectionId;
            api.updateRule({
                ruleId: src.id!,
                updateRuleRequest: {
                    sectionId: !dst.sectionId ? -1 : dst.sectionId
                },
            });
        }
        const orderedIds = rules.filter(x => x.sectionId === dst.sectionId).map(x => x.id!);
        api.updateScorecardRulesOrder({
            scorecardTemplateId: scorecardTemplate.value?.id!,
            requestBody: orderedIds
        });
    }
});

const {
    Reorderable: SectionReorderable,
} = useReorder<Section>((src, dst, isUnder) => {
    const sections = [...scorecardTemplate.value?.sections!];
    const srcIndex = sections.indexOf(src);
    const dstIndex = sections.indexOf(dst);
    const removed = sections.splice(srcIndex, 1);
    sections.splice(dstIndex + (isUnder ? 1 : 0), 0, ...removed);
    scorecardTemplate.value!.sections = sections;
    const orderedIds = sections.map(x => x.id!);
    api.reorderSections({
        scorecardTemplateId: scorecardTemplate.value?.id!,
        requestBody: orderedIds
    });
});

// Section management

const newSectionName = ref('');
const isNewSectionDialogOpen = ref(false);

function openNewSectionDialog() {
    newSectionName.value = '';
    isNewSectionDialogOpen.value = true;
}

const { load: createSection, loading: isCreatingSection } = useApiLoader(async () => {
    if (!scorecardTemplate.value) {
        return;
    }
    const res = await api.createSection({
        scorecardTemplateId: scorecardTemplate.value.id!,
        createSectionRequest: {
            name: newSectionName.value,
            isDraft: false,
        }
    });

    scorecardTemplate.value.sections!.push(res.data!);
    isNewSectionDialogOpen.value = false;
}, null);

async function onSectionEditKeyDown(e: KeyboardEvent, section: Section, closeCallback: () => void) {
    if (e.key === "Enter") {
        closeCallback();
        await api.updateSection({
            sectionId: section.id!,
            updateSectionRequest: {
                name: section.name
            },
        });
    } else if (e.key === "Escape") {
        closeCallback();
    }
}

const isMounted = useMounted();

const route = useRoute<'scorecard-template'>();

type NewRule = {
    name: string;
    isManual: boolean;
    isScriptAdherence: boolean;
    type: RuleType;
    maxScore: number;
    scriptSource: string;
    sectionId: number | null;
    isGlobal: boolean | null;
};

const isDrawerOpen = ref(false);
const selectedRule = ref<Rule | null>(null);
const newRule = ref<NewRule>({
    isManual: false,
    isScriptAdherence: false,
    type: RuleType.Default,
    maxScore: 10,
    name: '',
    isGlobal: false,
    scriptSource: "pass('PASS')",
    sectionId: null,
});

const { data: libs, load: loadTypes } = useApiLoader(async () => {
    const res = await api.getScorecardRuleScriptTypes();

    return {
        '@types/qa.d.ts': res,
    };
}, { '@types/qa.d.ts': '' });

const api = useApi();
const { resolve } = useRouter();

const { load: loadScorecardTemplate, data: scorecardTemplate, loading } = useApiLoader(async () => {
    const res = await api.getScorecardTemplate({
        scorecardTemplateId: parseInt(route.params.id, 10),
    });

    return res.data;
}, null);

const { load: saveNewRule, loading: isSaving } = useApiLoader(async () => {

    if (selectedRule.value) {
        await api.updateRule({
            ruleId: selectedRule.value.id!,
            updateRuleRequest: {
                name: newRule.value.name,
                maxScore: newRule.value.maxScore,
                isManual: newRule.value.isManual,
                isGlobal: newRule.value.isGlobal!,
                isScriptAdherence: newRule.value.isScriptAdherence,
                type: newRule.value.type,
                scriptContent: newRule.value.scriptSource,
            }
        });
    } else {
        await api.createRule({
            scorecardTemplateId: scorecardTemplate.value!.id!,
            createRuleRequest: {
                name: newRule.value.name,
                maxScore: newRule.value.maxScore,
                isManual: newRule.value.isManual,
                isGlobal: false,
                isScriptAdherence: newRule.value.isScriptAdherence,
                type: newRule.value.type,
                scriptContent: newRule.value.scriptSource,
            }
        });
    }

    const res = await api.getScorecardTemplate({
        scorecardTemplateId: parseInt(route.params.id, 10),
    });

    return res.data;
}, null);

function openRule(rule: Rule) {
    newRule.value = {
        name: rule.name!,
        isManual: rule.isManual!,
        isScriptAdherence: rule.isScriptAdherence!,
        type: rule.type!,
        maxScore: rule.maxScore!,
        scriptSource: rule.script?.content || '',
        sectionId: rule.sectionId!,
        isGlobal: rule.isGlobal!,
    };
    selectedRule.value = rule;
    isDrawerOpen.value = true;
}

function createNewRule(sectionId: number | null) {
    newRule.value = {
        isManual: false,
        isScriptAdherence: false,
        type: RuleType.Default,
        maxScore: 10,
        name: '',
        isGlobal: false,
        scriptSource: "pass('PASS')",
        sectionId: sectionId,
    };
    isDrawerOpen.value = true;
}

function exportTemplate() {
    if (!scorecardTemplate.value) {
        return;
    }

    const jsonStr = JSON.stringify(scorecardTemplate.value, null, 2);

    const blob = new Blob([jsonStr], { type: 'application/json' });

    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');

    a.href = url;
    a.download = scorecardTemplate.value.name + '.json';

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    URL.revokeObjectURL(url);
}

onMounted(async () => {
    loadTypes();
    await loadScorecardTemplate();
    setBreadcrumbs({
        crumbs: [resolve({ name: 'scorecard-templates' })],
        trailingTitle: scorecardTemplate.value?.name,
    });
});
definePage({
    name: "scorecard-template",
    meta: { title: "Scorecard Template", icon: "hierarchy-square" },
});
</script>